import { fetchAllRows, summarizeCurrencyAmounts, summarizeLearnerProgress } from "../utils/reporting.js";

// All admin learner reports use current enrollments across all platforms.
export function createAdminReportingQueries(client, { getExclusions, getSales }) {
  const without = (query, ids, column) => ids?.length
    ? query.not(column, "in", `(${ids.join(",")})`) : query;
  const getEnrollments = async (options = {}) => {
    const excluded = await getExclusions(options);
    return fetchAllRows(() => without(without(client.from("course_enrollments")
      .select("id, user_id, course_id, partner_id, payment_id, transaction_id, enrolled_at, completed_at, enrollment_status, progress_percentage, delivery_mode, accreditation_type, user:user_profiles!course_enrollments_user_id_fkey(full_name, email, created_at, account_type), course:courses(id, title), partner:partners(id, name, slug)")
      .in("enrollment_status", ["active", "completed"]), excluded.userIds, "user_id"), excluded.courseEnrollmentIds, "id"));
  };
  const getProgress = async (options = {}) => {
    const excluded = await getExclusions(options);
    return fetchAllRows(() => without(without(client.from("lesson_progress")
      .select("id, user_id, enrollment_id, last_accessed_at, time_spent_minutes"), excluded.userIds, "user_id"), excluded.courseEnrollmentIds, "enrollment_id"));
  };
  const getCourses = () => fetchAllRows(() => client.from("courses")
    .select("id, title, status, is_active, rating, total_ratings, created_at, category:course_categories(name)"));
  const rankCourses = (courses, enrollments, limit) => courses.filter((c) => c.is_active)
    .map((course) => {
      const rows = enrollments.filter((e) => e.course_id === course.id);
      return {
        id: course.id, title: course.title, rating: course.rating || 0,
        student_count: new Set(rows.map((e) => e.user_id)).size,
        completion_rate: rows.length ? Math.round(rows.filter((e) => e.enrollment_status === "completed").length / rows.length * 100) : 0,
      };
    }).sort((a, b) => b.student_count - a.student_count || a.title.localeCompare(b.title)).slice(0, limit);

  return {
    getEnrollments,
    async getOverview(options = {}) {
      const [courses, enrollments, sales] = await Promise.all([getCourses(), getEnrollments(options), getSales(options)]);
      const categories = new Map();
      courses.forEach((course) => {
        const name = course.category?.name || "Uncategorized";
        categories.set(name, (categories.get(name) || 0) + 1);
      });
      const rated = courses.filter((course) => Number(course.total_ratings) > 0);
      const ratingCount = rated.reduce((sum, c) => sum + Number(c.total_ratings), 0);
      const completed = enrollments.filter((e) => e.enrollment_status === "completed").length;
      return {
        totalCourses: courses.length,
        publishedCourses: courses.filter((c) => c.status === "published").length,
        draftCourses: courses.filter((c) => c.status === "draft").length,
        archivedCourses: courses.filter((c) => c.status === "archived").length,
        totalEnrollments: enrollments.length,
        total_students: new Set(enrollments.map((e) => e.user_id)).size,
        activeEnrollments: enrollments.filter((e) => e.enrollment_status === "active").length,
        completedEnrollments: completed,
        completion_rate: enrollments.length ? Math.round(completed / enrollments.length * 100) : 0,
        average_rating: ratingCount ? rated.reduce((sum, c) => sum + Number(c.rating || 0) * Number(c.total_ratings), 0) / ratingCount : null,
        revenue_by_currency: summarizeCurrencyAmounts(sales.filter((s) => s.status === "completed"), "total_amount"),
        categoryBreakdown: [...categories].map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count || a.name.localeCompare(b.name)),
        recentCourses: courses.slice().sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 6),
      };
    },
    async getTopCourses(limit = 10, options = {}) {
      const [courses, enrollments] = await Promise.all([getCourses(), getEnrollments(options)]);
      return rankCourses(courses, enrollments, limit);
    },
    async getStudents(timeframe = "30", options = {}) {
      const excluded = await getExclusions(options);
      const [enrollments, progress, courses, ratings] = await Promise.all([
        getEnrollments(options), getProgress(options), getCourses(),
        fetchAllRows(() => without(client.from("batch_ratings").select("id, user_id, overall_rating, enrollment:batch_enrollments(course_enrollment_id)"), excluded.userIds, "user_id")),
      ]);
      const start = new Date();
      start.setDate(start.getDate() - Number(timeframe));
      const eligibleRatings = ratings.filter((r) => !excluded.courseEnrollmentIds?.includes(r.enrollment?.course_enrollment_id));
      return {
        ...summarizeLearnerProgress(enrollments, progress, start),
        satisfaction_score: eligibleRatings.length ? Math.round(eligibleRatings.reduce((sum, r) => sum + Number(r.overall_rating), 0) / eligibleRatings.length * 10) / 10 : null,
        recent_activities: enrollments.map((e) => ({
          student_name: e.user?.full_name || "Student",
          action: e.enrollment_status === "completed" ? "completed" : "enrolled in",
          course_name: e.course?.title || "Course",
          at: e.enrollment_status === "completed" ? e.completed_at || e.enrolled_at : e.enrolled_at,
        })).sort((a, b) => new Date(b.at) - new Date(a.at)).slice(0, 10),
        top_courses: rankCourses(courses, enrollments, 10),
      };
    },
    async getUsers(options = {}) {
      const excluded = await getExclusions(options);
      const [users, enrollments, sales, progress] = await Promise.all([
        fetchAllRows(() => without(client.from("user_profiles").select("id, full_name, email, role, account_type, is_active, created_at, highest_qualification, company_name, industry_sector"), excluded.userIds, "id")),
        getEnrollments(options), getSales(options), getProgress(options),
      ]);
      const eligibleIds = new Set(enrollments.map((e) => e.id));
      return users.map((user) => {
        const enrolled = enrollments.filter((e) => e.user_id === user.id);
        const paid = sales.filter((s) => s.user_id === user.id && s.status === "completed");
        const completed = enrolled.filter((e) => e.enrollment_status === "completed").length;
        const dates = progress.filter((p) => p.user_id === user.id && eligibleIds.has(p.enrollment_id)).map((p) => p.last_accessed_at).filter(Boolean).sort((a,b) => new Date(b)-new Date(a));
        const paymentDates = paid.map((p) => p.paid_at || p.updated_at || p.created_at).filter(Boolean).sort((a,b) => new Date(b)-new Date(a));
        return {
          ...user, total_enrollments: enrolled.length, completed_courses: completed,
          revenue_by_currency: summarizeCurrencyAmounts(paid, "total_amount"),
          completion_rate: enrolled.length ? Math.round(completed / enrolled.length * 100) : 0,
          last_payment: paymentDates[0] || null,
          last_active: dates[0] || null,
        };
      }).sort((a,b) => new Date(b.last_active || 0) - new Date(a.last_active || 0));
    },
  };
}
